import {
  AI_AUTOMATION_TYPES,
  AI_AUTOMATION_USER_NAME,
  IN_TYPES,
  INSTAGRAM_REACTION_TYPE,
  META_TYPES,
  NO_RENDER_MESSAGE_TYPES,
  NOTE_TYPES,
} from 'apps/chat/components/ChatMessage/constants'
import { combineFBGuestLinkBlocks } from 'apps/chat/components/ChatMessage/helpers/blockHelpers'
import {
  WhatsAppMessageFailedErrorCodes,
  whatsAppMessageUndeliveredContent,
} from 'apps/chat/constants/chatConstants'
import { ChatMessageBlock, ChatMessageBlockInterface } from 'apps/chat/types/chatMessageBlockTypes'
import { QuestionAnswerMethod } from 'apps/chat/types/chatMessageCommonModelTypes'
import {
  MsginInstagramModelTypeStoryMention,
  MsginInstagramModelTypeStoryReplyTo,
} from 'apps/chat/types/chatMessageInstagramTypes'
import { ChatMessageBlockType } from 'apps/chat/types/chatMessagesBlockList'
import { ChatMessageType } from 'apps/chat/types/chatMessageTypeList'
import { ChatMessage, ChatMessageInterface } from 'apps/chat/types/chatMessageTypes'
import { WaThreadControl } from 'shared/api/requests/chat/threads/schemas'

export function isNoteMessage(
  message: ChatMessageInterface,
): message is ChatMessage.UserThreadNote {
  return NOTE_TYPES.includes(message.type)
}

export function isOutMessage(message: ChatMessageInterface) {
  return !IN_TYPES.includes(message.type)
}

export function isBotMessage(message: ChatMessageInterface) {
  return Boolean(
    isOutMessage(message) &&
      !isNoteMessage(message) &&
      message.type !== ChatMessageType.MSGOUT_LC &&
      message.type !== ChatMessageType.MSGOUT_LC_SMS &&
      message.type !== ChatMessageType.MSGOUT_LC_INSTAGRAM &&
      message.type !== ChatMessageType.MSGOUT_LC_WHATSAPP &&
      message.type !== ChatMessageType.MSGOUT_LC_TELEGRAM &&
      message.type !== ChatMessageType.MSGOUT_LC_TIKTOK,
  )
}

export function isTikTokAppEchoMessage(message: ChatMessageInterface): boolean {
  return message.type === ChatMessageType.MSGOUT_ECHO_TIKTOK
}

export function isWhatsAppEchoMessage(message: ChatMessageInterface): boolean {
  return message.type === ChatMessageType.MSGOUT_ECHO_WHATSAPP
}

// An echo is a message sent outside Manychat, which normally means the WhatsApp Business App. While
// the agent holds the thread it is the agent replying instead, and the messages carry nothing of
// their own to tell the two apart.
export function isMetaBusinessAgentMessage(
  message: ChatMessageInterface,
  threadControl: WaThreadControl | null | undefined,
): boolean {
  return threadControl === WaThreadControl.MBA && isWhatsAppEchoMessage(message)
}

export const isWhatsAppMessageUndelivered = (message: ChatMessageInterface) => {
  return (
    (message.type === ChatMessageType.MSGOUT_LC_WHATSAPP ||
      message.type === ChatMessageType.MSGOUT_WHATSAPP) &&
    message.model.messages?.some((message) => {
      return (
        message.error_code !== undefined &&
        Object.values(WhatsAppMessageFailedErrorCodes).includes(message.error_code)
      )
    })
  )
}

const isOutInstagramNotDeletedMessage = (
  message: ChatMessageInterface,
): message is ChatMessage.MsgoutInstagramNotDeleted =>
  message.type === ChatMessageType.MSGOUT_INSTAGRAM && !('is_deleted' in message.model)

export const isAiStepMessage = (message: ChatMessageInterface) => {
  return (
    (message.type === ChatMessageType.MSGOUT_TELEGRAM ||
      message.type === ChatMessageType.MSGOUT_WHATSAPP ||
      message.type === ChatMessageType.MSGOUT_KEYWORD ||
      message.type === ChatMessageType.MSGOUT_TIKTOK ||
      isOutInstagramNotDeletedMessage(message)) &&
    Array.isArray(message.model.messages) &&
    message.model.messages.some(
      (message) =>
        message.type === ChatMessageBlockType.QUESTION &&
        message.answer_method === QuestionAnswerMethod.AI_AGENT_INPUT,
    )
  )
}

export const isAiAutomationMessage = (message: ChatMessageInterface) => {
  return (
    isOutInstagramNotDeletedMessage(message) &&
    Array.isArray(message.model.messages) &&
    message.model.sender?.user_name === AI_AUTOMATION_USER_NAME
  )
}

export const isAiAutomationSystemMessage = (data: ChatMessageInterface) => {
  return AI_AUTOMATION_TYPES.includes(data.type)
}

export const getBlockErrorMessage = (block: ChatMessageBlockInterface) => {
  if ('error_code' in block && block.error_code !== undefined) {
    // Only WA has error_codes in message blocks
    const errorCode = block.error_code as WhatsAppMessageFailedErrorCodes

    return whatsAppMessageUndeliveredContent[errorCode]
  }
  return undefined
}

export const checkIsVoiceMessage = (blocks: ChatMessageBlockInterface[]) => {
  return blocks.some((block) => {
    if ('content' in block) {
      return Boolean(block.content?.data?.is_voice)
    }
    return false
  })
}

export const checkIsAudioMessage = (blocks: ChatMessageBlockInterface[]) => {
  return blocks.some((block) => {
    if ('content' in block) {
      return block.content?.data?.mime?.includes('audio')
    }
    return block?.type === ChatMessageBlockType.AUDIO
  })
}

export function isMetaMessage(message: ChatMessageInterface) {
  return META_TYPES.includes(message.type)
}

export function isEmptyMessage(message: ChatMessageInterface) {
  if (!('model' in message) || message?.model == null) {
    return true
  }

  // comment from 2017 - some messages doesn't have blocks due to backend bugs or smth
  if (
    'messages' in message.model &&
    Array.isArray(message.model.messages) &&
    message.model.messages.length === 0
  ) {
    return true
  }

  return false
}

export function isMmsMessage(message: ChatMessageInterface) {
  return message?.type === ChatMessageType.MSGOUT_BODY_MMS
}

export const isInstagramStory = (message: ChatMessageInterface) => {
  if (!('model' in message) || !('type' in message.model)) {
    return false
  }

  return [ChatMessageBlockType.STORY_REPLY_TO, ChatMessageBlockType.STORY_MENTION].includes(
    message.model.type,
  )
}

export const isInstagramCommentReply = (message: ChatMessageInterface) => {
  return message.type === ChatMessageType.MSGOUT_COMMENT_REPLY_INSTAGRAM
}

export const isOutInstagramMessage = (block: ChatMessageBlockInterface) => {
  return 'content' in block && Boolean(block.content)
}

export const isInInstagramStoryReplyTo = (
  block: ChatMessageBlockInterface,
): block is MsginInstagramModelTypeStoryReplyTo =>
  block.type === ChatMessageBlockType.STORY_REPLY_TO && !isOutInstagramMessage(block)

export const isInInstagramStoryMention = (
  block: ChatMessageBlockInterface,
): block is MsginInstagramModelTypeStoryMention =>
  block.type === ChatMessageBlockType.STORY_MENTION && !isOutInstagramMessage(block)

export const isInInstagramPostShare = (
  block: ChatMessageBlockInterface,
): block is MsginInstagramModelTypeStoryMention =>
  block.type === ChatMessageBlockType.SHARE && !isOutInstagramMessage(block)

export function isMessageReacted({
  message,
  rawMessageBlocks,
  isOutMessage,
}: {
  message: ChatMessageInterface
  rawMessageBlocks: ChatMessageBlockInterface[]
  isOutMessage: boolean
}): boolean {
  let reactions

  if (isOutMessage && 'reactions' in rawMessageBlocks[0]) {
    reactions = rawMessageBlocks[0].reactions
  } else if (!isOutMessage && 'reactions' in message) {
    reactions = message.reactions
  }

  if (!reactions) {
    return false
  }

  return reactions.includes(INSTAGRAM_REACTION_TYPE)
}

export function getRawMessageBlocks(message: ChatMessageInterface): ChatMessageBlockInterface[] {
  if ('model' in message && 'messages' in message.model && Array.isArray(message.model.messages)) {
    return message.model.messages
  }

  const model = 'model' in message ? message.model : {}
  let type = message.type as unknown as ChatMessageBlockInterface['type']

  if ('model' in message && 'type' in message.model) {
    type = message.model.type
  }

  // not sure if it is possible to legitimately type this kind of construction
  // the problem is with flattening message and the resulted
  // partial intersections of ChatMessageType and ChatBlockType
  return [{ ...model, type }] as ChatMessageBlockInterface[]
}

export const createMMSBlocks = (message: ChatMessageBlock.Mms): ChatMessageBlockInterface[] => {
  // Render MMS as two blocks: Image block + Sms block
  const blocks: ChatMessageBlockInterface[] = []

  const [data] = message.content
  const { media_url, media_type, body } = data

  if (media_type === 'image') {
    blocks.push({ type: ChatMessageBlockType.IMAGE, url: media_url })
  }

  if (body) {
    blocks.push({ type: ChatMessageBlockType.SMS, text: body })
  }

  return blocks
}

export const isMessageDeleted = (block: ChatMessageBlockInterface) => {
  return Boolean('is_deleted' in block && block?.is_deleted)
}

export const createBlocksFromMessage = (
  message: ChatMessageBlockInterface,
): ChatMessageBlockInterface[] => {
  if (message.type === ChatMessageBlockType.MMS) {
    return createMMSBlocks(message)
  }

  if ([ChatMessageBlockType.MSGIN_INSTAGRAM].includes(message.type) && isMessageDeleted(message)) {
    return [{ type: ChatMessageBlockType.INSTAGRAM_DELETED }]
  }

  if (
    isMessageDeleted(message) &&
    [
      ChatMessageBlockType.MSGOUT_INSTAGRAM,
      ChatMessageBlockType.MSGOUT_LC_INSTAGRAM,
      ChatMessageBlockType.MSGOUT_ECHO_INSTAGRAM,
    ].includes(message.type)
  ) {
    return [{ type: ChatMessageBlockType.INSTAGRAM_DELETED_OUT }]
  }

  return [message]
}

export const filterNoRenderMessages = (messages: ChatMessageBlockInterface[]) => {
  return messages.filter((message) => !NO_RENDER_MESSAGE_TYPES.includes(message.type))
}

export const messagesToBlocks = (messages: ChatMessageBlockInterface[]) => {
  messages = filterNoRenderMessages(messages)

  const blocks = messages.reduce(
    (res, message) => res.concat(createBlocksFromMessage(message)),
    [] as ChatMessageBlockInterface[],
  )

  return combineFBGuestLinkBlocks(blocks)
}
